// Substitutions applied to text just before it is handed to speech
// synthesis, so the voice says something intelligible. See
// 'getAudioText' in 'useSkuUtils', the only caller.
//
// A substitution is not one kind of thing, so the rules sit in three
// buckets:
//
//   neutral - punctuation, spacing, digits, brand names, and English UI
//     tokens that survive translation untouched. Nothing here depends
//     on a language, so it always runs.
//
//   expand - an abbreviation spelled out as words. The words have to be
//     in the language of the *text*, so this is keyed by the locale the
//     shopper is reading. An English 'ounces' read to a Spanish
//     listener is a step backwards from the bare 'oz' they saw.
//
//   respell - a word deliberately misspelled so the engine's
//     letter-to-sound rules land somewhere useful. Keyed by text
//     language too, because the pattern has to match the text.
//     Strictly the *replacement* belongs to the voice's language -
//     'skew' only reads correctly through an English engine - but every
//     entry below has held up across the voices tested so far, so there
//     is no per-voice dimension yet. Add one when a voice disproves
//     that, not before.
//
// Each rule is [ pattern, replacement ]. They run in array order and
// later patterns see earlier replacements, so order within a bucket
// matters; the buckets themselves run neutral, expand, respell.
export const AUDIO_SUBS = {

  neutral: [
    [ /[ ][-][ ]/g, ' ! ' ],
    [ /[ ]\/\//g,   ' !'  ],
      // a bang (!) induces pause effect. "!.!" longer pause, but too long!
    [ /[ ]?[(][?i>][)]/g, '' ],
      // rm instances of '(?)' and '(i)' - images for Alert and Info icon
      // Also rm '(>)' which is the marker for a Circle with no subscribers.
    [ /\bLoom, Inc\b/g, 'Loom Inc'    ],
    [ /\bShopWeAll\b/g, 'Shop-We-All' ],
      // Brand names come through translation unchanged, so they need the
      // same handling in every locale.
    [ /\bAsOf\b/g,     'As Of'         ],
    [ /\bamtToGet\b/g, 'Amount To Get' ],
      // Field names shown in the UI. They stay English in es and ht too
      // (grep the locale files), so splitting the camel case is right in
      // every language - the words were already going to be English.
      // Digit runs are deliberately absent. Spacing them out ('3, 3, 9,
      // ...') is what stops an engine reading an identifier as a
      // magnitude, but telling an identifier from a quantity is a
      // judgement no pattern can make: the same seven digits want one
      // reading as a store code and the other as a count. So example
      // identifiers are interpolated into the help text from the
      // 'FAQ_*' constants in './index', which carry a '_SPOKEN' form
      // alongside the displayed one.
  ],

  expand: {
    en: [
      [ /ft2/g,             'square feet'   ],
      [ /\boz\b/ig,         'ounces'        ],
      [ /\blb\b/g,          'pounds'        ],
      [ /\bgl\b/g,          'gallon'        ],
      [ /\bL\b/g,           'liter'         ],
      [ /\bUnit[ ]?Qty\b/g, 'Unit Quantity' ],
      [ /\bckBox\b/g,       'checkbox'      ],
      [ /e[.]g[.][,]?/g,    'for example,'  ],
        // adding ',' after 'for example' might get effect of audio pause
      [ /i[.]e[.]/g,        'that is,'      ],
    ],
    es: [
      [ /ft2/g,     'pies cuadrados' ],
      [ /\boz\b/ig, 'onzas'          ],
      [ /\blb\b/g,  'libras'         ],
      [ /\bgl\b/g,  'galón'          ],
      [ /\bL\b/g,   'litro'          ],
      [ /\bp[.][ ]?ej[.][,]?/g, 'por ejemplo,' ],
    ],
    ht: [
      [ /ft2/g,     'pye kare' ],
      [ /\boz\b/ig, 'ons'      ],
      [ /\blb\b/g,  'liv'      ],
      [ /\bgl\b/g,  'galon'    ],
      [ /\bL\b/g,   'lit'      ],
      [ /\bpa ?eg[.][,]?/g, 'pa egzanp,' ],
    ],
  },

  respell: {
    en: [
      [ /\ba\b/g,              'A'              ],
      [ /\blocale\b/g,         'low-CAL'        ],
      [ /\b[Ss]ku/g,           'skew'           ],
      [ /\bok\b/gi,            'okay'           ],
      [ /\bshould lead to\b/g, 'should leed to' ],
      [ /\bthat read CSV\b/g,  'that reed CSV'  ],
      [ /\bread on[.]/g,       ' reed on.'      ],
      [ /\bPrice record\b/g,   'Price wreckird' ],
      [ /\breconfigure\b/g,    're-configure'   ],
      [ /\bre[-]price\b/g,     'ree price'      ],
    ],
    // Nothing here yet for es or ht. 'Sku' and 'ok' do survive
    // translation, so they still reach a Spanish or Haitian Creole voice
    // unrespelled - but what they ought to become there is untested, and
    // guessing would trade a known-mediocre reading for an unknown one.
    // Fill these in from listening, one entry at a time.
    es: [],
    ht: [],
  },

};

// 'textLang' is a bare language tag ('en', 'es'), not a locale - callers
// should trim the region off first. A language with no bucket of its own
// gets the neutral rules and nothing else, which is the safe default: no
// substitution beats a substitution in the wrong language.
export const applyAudioSubs = ({ text = "", textLang = 'en' }) =>
  [].concat(
    AUDIO_SUBS.neutral,
    AUDIO_SUBS.expand[textLang]  || [],
    AUDIO_SUBS.respell[textLang] || [],
  ).reduce(
    (soFar, [ pattern, replacement ]) => soFar.replace(pattern, replacement),
    text
  );
